class GridCarouselComponent extends HTMLElement {
    connectedCallback() {
        this.scroller = this.querySelector('[data-grid-carousel-scroller]');
        this.previousButton = this.querySelector('[data-grid-carousel-previous]');
        this.nextButton = this.querySelector('[data-grid-carousel-next]');
        this.mediaQuery = window.matchMedia('(min-width: 750px)');

        this.addEventListener('click', this.handleClick);
        this.scroller?.addEventListener('scroll', this.handleScroll, { passive: true });
        this.mediaQuery.addEventListener('change', this.updateControls);

        this.resizeObserver = new ResizeObserver(this.updateControls);
        if (this.scroller) this.resizeObserver.observe(this.scroller);

        this.updateControls();
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.scroller?.removeEventListener('scroll', this.handleScroll);
        this.mediaQuery?.removeEventListener('change', this.updateControls);
        this.resizeObserver?.disconnect();
    }

    get isCarouselActive() {
        const layout = this.mediaQuery.matches ? this.dataset.desktopLayout : this.dataset.mobileLayout;

        return layout === 'carousel';
    }

    get canScroll() {
        if (!this.scroller) return false;

        return this.scroller.scrollWidth > this.scroller.clientWidth + 1;
    }

    handleClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const action = target.closest('[data-grid-carousel-action]')?.getAttribute('data-grid-carousel-action');
        if (!action) return;

        event.preventDefault();

        if (action === 'previous') {
            this.scrollToSlide(-1);
            return;
        }

        this.scrollToSlide(1);
    };

    handleScroll = () => {
        window.requestAnimationFrame(this.updateControls);
    };

    getSlidePositions() {
        if (!this.scroller) return [];

        const slides = Array.from(this.scroller.children)
            .filter((child) => child instanceof HTMLElement)
            .map((child) => child.offsetLeft);

        const firstPosition = slides[0] ?? 0;

        return slides.map((position) => position - firstPosition);
    }

    scrollToSlide(direction) {
        if (!this.scroller || !this.isCarouselActive) return;

        const positions = this.getSlidePositions();
        if (!positions.length) return;

        const current = this.scroller.scrollLeft;
        const target = direction < 0
            ? [...positions].reverse().find((position) => position < current - 1)
            : positions.find((position) => position > current + 1);

        this.scroller.scrollTo({
            left: target ?? current,
            behavior: 'smooth',
        });
    }

    updateControls = () => {
        if (!this.previousButton || !this.nextButton || !this.scroller) return;

        const shouldShowControls = this.isCarouselActive && this.canScroll;
        this.toggleAttribute('data-grid-carousel-controls-hidden', !shouldShowControls);

        if (!shouldShowControls) {
            this.previousButton.disabled = true;
            this.nextButton.disabled = true;
            return;
        }

        const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
        const scrollLeft = this.scroller.scrollLeft;

        this.previousButton.disabled = scrollLeft <= 1;
        this.nextButton.disabled = scrollLeft >= maxScroll - 1;
    };
}

if (!customElements.get('grid-carousel-component')) {
    customElements.define('grid-carousel-component', GridCarouselComponent);
}
